/* eslint-disable max-lines */
import { CustomIntegrationSchema, DefaultIntegrationSchema } from '@omnea-digital/extops-schema'
import isNil from 'lodash/isNil'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'

export const categories = [
  'Ticketing',
  'E-Signature',
  'CLM',
  'Accounting',
  'Communication',
  'GRC',
  'TPRM',
  'File storage',
  'NaaS',
  'Asset Management',
  'ESG',
  'Public Api',
  'AI',
  'Pricing intelligence',
  'Other',
] as const

export type IntegrationCategory = (typeof categories)[number]

type IntegrationSource = ['ExtOps', 'Accounting', 'Native'][number]

export type SupportedIconColors = 'primary' | 'secondary' | 'grey'

// TODO EXP-1388 move this to extops shared variables
// Keep in sync with: https://www.notion.so/omnea/13788cce658780a09e22ecaaccce3480
const clmIntegrations = [
  'ironclad',
  'pandadoc',
  'juro',
  'docusign',
  'docusign-clm',
  'linksquares',
  'tomorro',
  'contractpodai',
  'conga',
  'agiloft',
  'contractworks',
  'coupa-clm',
  'conga-contracts',
] as const

const chatIntegrations = ['microsoft-teams', 'slack'] as const

export const accountingIntegrations = [
  'netsuite',
  'workday',
  'microsoft-dynamics-fo',
  'coupa',
  'sap-4hana',
  'microsoft-dynamics-business-central',
] as const

export const accountingReferenceIdFallback: Record<(typeof accountingIntegrations)[number], string> = {
  netsuite: 'netsuite',
  'microsoft-dynamics-fo': 'dynamics_fno',
  workday: 'workday',
  coupa: 'coupa',
  'sap-4hana': 'sap_s4hana',
  'microsoft-dynamics-business-central': 'microsoft-dynamics-business-central',
}

export const resolveAccountingReferenceId = (
  integration: AccountingIntegration,
  referenceId: string | null | undefined
): string => referenceId ?? accountingReferenceIdFallback[integration]

const fileStorageIntegrations = ['google-drive'] as const

const grcIntegrations = ['dun-and-bradstreet', 'creditsafe', 'thomson-reuters', 'trulioo'] as const

const naasIntegrations = ['sastrify'] as const

const otherIntegrations = [
  'zapier',
  'mesh',
  'albertsons',
  'noop', // TODO EXP-1386 Remove noop from global integrations
] as const

const tprmIntegrations = ['upguard', 'onetrust', 'drata', 'process-unity', 'tprm-continuous-monitoring'] as const

const ticketingIntegrations = [
  'freshservice',
  'jira',
  'zendesk',
  'asana',
  'freshdesk',
  'intercom',
  'jira-service-management',
  'servicenow',
  'trello',
] as const

const assetManagementIntegrations = ['snipe-it', 'bluetally'] as const

const esgIntegrations = ['ecovadis'] as const

const esignIntegrations = ['adobe-sign'] as const

const nativeIntegrations = ['microsoft-teams', 'slack'] as const

const httpIntegrations = ['http'] as const
const sftpIntegrations = ['sftp'] as const
export const customIntegrations = [...httpIntegrations, ...sftpIntegrations] as const

export const publicApiIntegrations = ['public-api'] as const

const aiIntegrations = ['ai'] as const

const pricingIntelligenceIntegrations = ['tropic'] as const

const punchoutIntegrations = ['punchout-generic', 'punchout-amazon-business', 'punchout-unite', 'punchout-cdw'] as const

const extOpsIntegrations = [
  ...DefaultIntegrationSchema.options.map(({ value }) => value),
  ...CustomIntegrationSchema.options.map(({ value }) => value),
]

export const globalIntegrations = [
  ...clmIntegrations,
  ...chatIntegrations,
  ...accountingIntegrations,
  ...fileStorageIntegrations,
  ...grcIntegrations,
  ...naasIntegrations,
  ...otherIntegrations,
  ...tprmIntegrations,
  ...ticketingIntegrations,
  ...assetManagementIntegrations,
  ...esgIntegrations,
  ...esignIntegrations,
  ...customIntegrations,
  ...publicApiIntegrations,
  ...aiIntegrations,
  ...pricingIntelligenceIntegrations,
  ...punchoutIntegrations,
] as const

export type AccountingIntegration = (typeof accountingIntegrations)[number]
export type ExtOpsIntegration = (typeof extOpsIntegrations)[number]
export type GlobalIntegration = (typeof globalIntegrations)[number]
export type NativeIntegration = (typeof nativeIntegrations)[number]
export type CustomIntegration = (typeof customIntegrations)[number]
export type HttpIntegration = (typeof httpIntegrations)[number]
export type SftpIntegration = (typeof sftpIntegrations)[number]
export type PublicApiIntegration = (typeof publicApiIntegrations)[number]
export type PunchoutIntegration = (typeof punchoutIntegrations)[number]
export type PricingIntelligenceIntegration = (typeof pricingIntelligenceIntegrations)[number]

export const isAccountingIntegration = (integration: string | undefined | null): integration is AccountingIntegration => {
  if (isNil(integration)) return false
  return accountingIntegrations.includes(integration as AccountingIntegration)
}

export const isExtOpsIntegration = (integration: string): integration is ExtOpsIntegration => {
  return extOpsIntegrations.includes(integration as ExtOpsIntegration)
}

export const isIntegrationSupportingSettings = (integration: string): boolean =>
  isAccountingIntegration(integration) || isExtOpsIntegration(integration) || isNativeIntegration(integration)

export const isGlobalIntegration = (integration: string): integration is GlobalIntegration => {
  return globalIntegrations.includes(integration as GlobalIntegration)
}

export const isNativeIntegration = (integration: string): integration is NativeIntegration => {
  return nativeIntegrations.includes(integration as NativeIntegration)
}

export const isCustomIntegration = (integration: string | undefined): integration is CustomIntegration =>
  isDefined(integration) && customIntegrations.includes(integration as CustomIntegration)

export const isHttpIntegration = (integration: string | undefined): integration is HttpIntegration =>
  isDefined(integration) && httpIntegrations.includes(integration as HttpIntegration)

export const isSftpIntegration = (integration: string | undefined): integration is SftpIntegration =>
  isDefined(integration) && sftpIntegrations.includes(integration as SftpIntegration)

export const isPublicApiIntegration = (integration: string | undefined): integration is PublicApiIntegration =>
  isDefined(integration) && publicApiIntegrations.includes(integration as PublicApiIntegration)

export const isPunchoutIntegration = (integration: string | undefined | null): integration is PunchoutIntegration => {
  if (isNil(integration)) return false
  return (punchoutIntegrations as readonly string[]).includes(integration)
}

export const isSupportedForSelfServe = (integration: string): boolean =>
  isAccountingIntegration(integration) ||
  isExtOpsIntegration(integration) ||
  isNativeIntegration(integration) ||
  isPublicApiIntegration(integration)

export type GlobalIntegrationMetadata = {
  integration: GlobalIntegration
  operation: string
  title: string
  blockLabel?: string
  menuLabel?: string
  description: string
  icon: (color: SupportedIconColors) => string
  logo: string // src of the logo, 114x20 pixels svg
  isLive: boolean
  isInRoadmap: boolean
  categories: IntegrationCategory[]
  integrationSource: IntegrationSource
  additionalMetadata?: Record<string, string>
  isOmnean?: boolean
}

export type CustomIntegrationMetadata = GlobalIntegrationMetadata & {
  integration: CustomIntegration
}

// Global integrations we have live or planned. They should all be added here.
// Tracks details from:
// https://www.notion.so/omnea/13788cce658780a09e22ecaaccce3480
export const globalIntegrationsMetadata: Record<GlobalIntegration, GlobalIntegrationMetadata> = {
  // CLM
  ironclad: {
    integration: 'ironclad',
    operation: 'ironclad-complete-workflow',
    title: 'Ironclad',
    description: 'Upload a contract and critical metadata to Ironclad',
    icon: () => '/app/integrations/icons/ironclad-primary.png',
    logo: '/app/integrations/square-icons/Ironclad.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['CLM'],
  },
  pandadoc: {
    integration: 'pandadoc',
    operation: 'pandadoc-create-agreement',
    title: 'PandaDoc',
    description: 'Upload a contract and critical metadata to PandaDoc',
    icon: () => '/app/integrations/square-icons/PandaDoc.svg',
    logo: '/app/integrations/square-icons/PandaDoc.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['CLM'],
  },
  juro: {
    integration: 'juro',
    operation: '',
    title: 'Juro',
    description: 'Upload a contract and critical metadata to Juro',
    icon: () => '',
    logo: '/app/integrations/square-icons/Juro.svg',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['CLM'],
  },
  docusign: {
    integration: 'docusign',
    operation: 'docusign-esign-create-envelope',
    title: 'DocuSign eSign',
    description: 'Upload a document to DocuSign eSign and wait for it to be signed',
    icon: () => '/app/integrations/icons/docusign-primary.png',
    logo: '/app/integrations/square-icons/DocuSign.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['CLM', 'E-Signature'],
    additionalMetadata: {
      individualConsentUrl:
        'https://account.docusign.com/oauth/auth?response_type=code&scope=signature%20impersonation&client_id=9a5f778c-a021-44b1-9ba0-83694cf997a9',
      organizationConsentUrl:
        'https://account.docusign.com/oauth/auth?response_type=code&scope=openid&client_id=9a5f778c-a021-44b1-9ba0-83694cf997a9&admin_consent_scope=signature%20impersonation',
    },
  },
  'docusign-clm': {
    integration: 'docusign-clm',
    operation: 'docusign-clm-upload-document',
    title: 'DocuSign CLM',
    description: 'Upload contracts and metadata to DocuSign CLM.',
    icon: () => '/app/integrations/icons/docusign-primary.png',
    logo: '/app/integrations/square-icons/DocuSign.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['CLM'],
  },
  linksquares: {
    integration: 'linksquares',
    operation: 'linksquares-create-agreement',
    title: 'LinkSquares',
    description:
      'Speed up legal approvals by uploading contracts and associated metadata to LinkSquares from your Omnea workflow.',
    icon: () => '/app/integrations/square-icons/LinkSquares.svg',
    logo: '/app/integrations/square-icons/LinkSquares.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['CLM'],
  },
  tomorro: {
    integration: 'tomorro',
    operation: '',
    title: 'Tomorro',
    description: 'Upload a contract and critical metadata to Tomorro',
    icon: () => '',
    logo: '/app/integrations/square-icons/Tomorro.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['CLM'],
  },
  contractpodai: {
    integration: 'contractpodai',
    operation: 'contractpodai-create-contract',
    title: 'ContractPodAI',
    description: 'Upload a contract and critical metadata to ContractPodAI',
    icon: () => '/app/integrations/square-icons/ContractPodAI.svg',
    logo: '/app/integrations/square-icons/ContractPodAI.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['CLM'],
  },
  conga: {
    integration: 'conga',
    operation: '',
    title: 'Conga',
    description: 'Upload a contract and critical metadata to Conga',
    icon: () => '',
    logo: '/app/integrations/square-icons/Conga.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['CLM'],
  },
  agiloft: {
    integration: 'agiloft',
    operation: '',
    title: 'Agiloft',
    description: 'Upload a contract and critical metadata to Agiloft',
    icon: () => '',
    logo: '/app/integrations/square-icons/AgiLoft.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['CLM'],
  },
  contractworks: {
    integration: 'contractworks',
    operation: '',
    title: 'ContractWorks',
    description: 'Upload a contract and critical metadata to ContractWorks',
    icon: () => '',
    logo: '/app/integrations/square-icons/Contractworks.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['CLM'],
  },
  'coupa-clm': {
    integration: 'coupa-clm',
    operation: 'coupa-clm-contract',
    title: 'Coupa CLM',
    description: 'Create a contract and upload metadata to Coupa CLM',
    icon: () => '/app/integrations/square-icons/Coupa.svg',
    logo: '/app/integrations/square-icons/Coupa.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['CLM'],
  },
  'conga-contracts': {
    integration: 'conga-contracts',
    operation: 'conga-contracts-create-contract',
    title: 'Conga Contracts',
    description: 'Upload a contract and critical metadata to Conga Contracts',
    icon: () => '/app/integrations/square-icons/Conga.svg',
    logo: '/app/integrations/square-icons/Conga.svg',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['CLM'],
  },
  // Chat
  slack: {
    integration: 'slack',
    operation: '',
    title: 'Slack',
    description: 'Launch new requests and receive notifications in Slack',
    icon: () => '',
    logo: '/app/integrations/square-icons/Slack.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'Native',
    categories: ['Communication'],
  },
  'microsoft-teams': {
    integration: 'microsoft-teams',
    operation: '',
    title: 'Microsoft Teams',
    description: 'Launch new requests and receive notifications in Microsoft Teams',
    icon: () => '',
    logo: '/app/integrations/square-icons/Teams.svg',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'Native',
    categories: ['Communication'],
  },
  // ERP
  netsuite: {
    integration: 'netsuite',
    operation: '',
    title: 'NetSuite',
    description: 'Connect your NetSuite instance to sync POs and suppliers',
    icon: () => '',
    logo: '/app/integrations/square-icons/Netsuite-2.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'Accounting',
    categories: ['Accounting'],
  },
  workday: {
    integration: 'workday',
    operation: '',
    title: 'Workday',
    description: 'Connect your Workday instance to sync POs and suppliers',
    icon: () => '',
    logo: '/app/integrations/square-icons/Workday.svg',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'Accounting',
    categories: ['Accounting'],
  },
  'microsoft-dynamics-fo': {
    integration: 'microsoft-dynamics-fo',
    operation: '',
    title: 'Microsoft Dynamics F&O',
    description: 'Connect your Microsoft Dynamics F&O instance to sync POs and suppliers',
    icon: () => '',
    logo: '/app/integrations/square-icons/Dynamics F&O.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'Accounting',
    categories: ['Accounting'],
  },
  'microsoft-dynamics-business-central': {
    integration: 'microsoft-dynamics-business-central',
    operation: '',
    title: 'Microsoft Dynamics Business Central',
    description: 'Connect your Microsoft Dynamics Business Central instance to sync POs and suppliers',
    icon: () => '',
    logo: '/app/integrations/square-icons/Business Central.svg',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'Accounting',
    categories: ['Accounting'],
  },
  'sap-4hana': {
    integration: 'sap-4hana',
    operation: '',
    title: 'SAP 4HANA',
    description: 'Connect your SAP 4HANA instance to sync POs and suppliers',
    icon: () => '',
    logo: '/app/integrations/square-icons/SAP 4HANA.svg',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'Accounting',
    categories: ['Accounting'],
  },
  coupa: {
    integration: 'coupa',
    operation: '',
    title: 'Coupa',
    description: 'Connect your Coupa instance to sync POs and suppliers',
    icon: () => '',
    logo: '/app/integrations/square-icons/Coupa.svg',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'Accounting',
    categories: ['Accounting'],
  },
  // File storage
  'google-drive': {
    integration: 'google-drive',
    operation: 'google-drive-upload-file',
    title: 'Google Drive',
    description: 'Connect your Google Drive instance to sync files',
    icon: () => '/app/integrations/square-icons/Google Drive.svg',
    logo: '/app/integrations/square-icons/Google Drive.svg',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['File storage'],
  },
  // GRC
  'dun-and-bradstreet': {
    integration: 'dun-and-bradstreet',
    operation: '',
    title: 'Dun & Bradstreet',
    description: 'Connect your Dun & Bradstreet instance to sync risk scores',
    icon: () => '',
    logo: '/app/integrations/square-icons/Dun and Bradstreet.svg',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['GRC'],
  },
  creditsafe: {
    integration: 'creditsafe',
    operation: 'creditsafe-generate-report',
    title: 'CreditSafe',
    description: 'Connect your CreditSafe instance to sync risk scores',
    icon: () => '/app/integrations/square-icons/CreditSafe.svg',
    logo: '/app/integrations/square-icons/CreditSafe.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['GRC'],
  },
  'thomson-reuters': {
    integration: 'thomson-reuters',
    operation: 'thomson-reuters-generate-report',
    title: 'Thomson Reuters',
    description: 'Connect your Thomson Reuters instance to sync risk scores',
    icon: () => '/app/integrations/square-icons/Thomson Reuters.svg',
    logo: '/app/integrations/square-icons/Thomson Reuters.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['GRC'],
  },
  trulioo: {
    integration: 'trulioo',
    operation: 'trulioo-trigger-workflow',
    title: 'Trulioo',
    description: 'Connect to Trulioo to trigger identity verification workflows and capture the results',
    icon: () => '/app/integrations/square-icons/Trulioo.png',
    logo: '/app/integrations/square-icons/Trulioo.png',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['GRC'],
  },
  // NaaS
  sastrify: {
    integration: 'sastrify',
    operation: '',
    title: 'Sastrify',
    description: 'A Sastrify integration allows price benchmarks to be displayed in Omnea when purchase a new vendor',
    icon: () => '',
    logo: '/app/integrations/square-icons/Sastrify.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['NaaS'],
  },
  // Other
  mesh: {
    integration: 'mesh',
    operation: 'mesh-create-virtual-card',
    title: 'Mesh Payments',
    description: 'Connect your Mesh instance to create virtual cards',
    icon: () => '/app/integrations/square-icons/Mesh.svg',
    logo: '/app/integrations/square-icons/Mesh.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['Other'],
  },
  albertsons: {
    integration: 'albertsons',
    operation: 'albertsons-convert-to-csv',
    title: 'Albertsons Convert to CSV',
    description: 'Convert request data to CSV',
    icon: () => '/app/integrations/icons/albertsons.svg',
    logo: '/app/integrations/icons/albertsons.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['Other'],
  },
  noop: {
    integration: 'noop', // TODO EXP-1386 Remove noop from global integrations
    operation: 'noop',
    title: 'Noop',
    description: 'No operation, used for testing',
    icon: (color) => `/app/integrations/icons/default-${color}.png`,
    logo: '/app/integrations/logos/asana.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['Communication'],
  },
  zapier: {
    integration: 'zapier',
    operation: 'zapier-execute-zap',
    title: 'Zapier',
    description: 'Trigger Zapier workflows directly from Omnea',
    icon: (color) => `/app/integrations/icons/default-${color}.png`,
    logo: '/app/integrations/square-icons/Zapier.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['Other'],
  },
  // TPRM
  upguard: {
    integration: 'upguard',
    operation: '',
    title: 'UpGuard',
    description: 'Connect your UpGuard instance to manage third party risk',
    icon: () => '',
    logo: '/app/integrations/square-icons/UpGuard.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['TPRM'],
  },
  onetrust: {
    integration: 'onetrust',
    operation: 'onetrust-create-assessment',
    title: 'OneTrust',
    description: 'Connect your One Trust instance to manage third party risk',
    icon: () => '/app/integrations/square-icons/OneTrust.svg',
    logo: '/app/integrations/square-icons/OneTrust.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['TPRM'],
  },
  drata: {
    integration: 'drata',
    operation: '',
    title: 'Drata',
    description: 'Connect your Drata instance to manage third party risk',
    icon: () => '',
    logo: '/app/integrations/square-icons/Drata.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['TPRM'],
  },
  'process-unity': {
    integration: 'process-unity',
    operation: 'process-unity-import-export',
    title: 'Process Unity',
    description: 'Connect your Process Unity instance to manage third party risk',
    icon: () => '/app/integrations/square-icons/ProcessUnity.svg',
    logo: '/app/integrations/square-icons/ProcessUnity.svg',
    isLive: true,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['TPRM'],
  },
  'tprm-continuous-monitoring': {
    integration: 'tprm-continuous-monitoring',
    operation: 'tprm-continuous-monitoring-point-in-time-check',
    title: 'Risk screening',
    blockLabel: 'Point-in-time risk check',
    menuLabel: 'Risk Screening - Point-in-time',
    description: 'Check historical risk data for your suppliers with Risk screening',
    icon: () => '/app/integrations/square-icons/DowJonesSquare.svg',
    logo: '/app/integrations/square-icons/DowJonesSquare.svg',
    isLive: true,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['TPRM'],
  },
  // Ticketing
  freshservice: {
    integration: 'freshservice',
    operation: '',
    title: 'Freshservice',
    description: 'Connect your Freshservice instance to sync tickets',
    icon: () => '',
    logo: '/app/integrations/square-icons/Freshservice.svg',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['Ticketing'],
  },
  jira: {
    integration: 'jira',
    operation: 'jira-create-ticket',
    title: 'Jira',
    description: 'Connect your Jira instance to sync tickets',
    icon: (color) => `/app/integrations/icons/jira-${color}.png`,
    logo: '/app/integrations/square-icons/Jira.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['Ticketing'],
  },
  zendesk: {
    integration: 'zendesk',
    operation: 'zendesk-create-ticket',
    title: 'Zendesk',
    description: 'Connect your Zendesk instance to sync tickets',
    icon: () => '/app/integrations/square-icons/Zendesk.svg',
    logo: '/app/integrations/square-icons/Zendesk.svg',
    isLive: true,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['Ticketing'],
  },
  asana: {
    integration: 'asana',
    operation: '',
    title: 'Asana',
    description: 'Connect your Asana instance to sync tickets',
    icon: (color) => `/app/integrations/icons/asana-${color}.png`,
    logo: '/app/integrations/square-icons/Asana.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['Ticketing'],
  },
  freshdesk: {
    integration: 'freshdesk',
    operation: '',
    title: 'Freshdesk',
    description: 'Connect your Freshdesk instance to sync tickets',
    icon: () => '',
    logo: '/app/integrations/square-icons/Freshdesk.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['Ticketing'],
  },
  intercom: {
    integration: 'intercom',
    operation: '',
    title: 'Intercom',
    description: 'Connect your Intercom instance to sync tickets',
    icon: () => '',
    logo: '/app/integrations/square-icons/Intercom.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['Ticketing'],
  },
  'jira-service-management': {
    integration: 'jira-service-management',
    operation: 'jira-service-management-create-ticket',
    title: 'Jira Service Management',
    description: 'Connect your Jira Service Management instance to sync tickets',
    icon: () => '/app/integrations/icons/jira_service_management_icon.png',
    logo: '/app/integrations/square-icons/Jira Service Management.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['Ticketing'],
  },
  servicenow: {
    integration: 'servicenow',
    operation: '',
    title: 'ServiceNow',
    description: 'Connect your ServiceNow instance to sync tickets',
    icon: () => '',
    logo: '/app/integrations/square-icons/ServiceNow.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['Ticketing'],
  },
  trello: {
    integration: 'trello',
    operation: '',
    title: 'Trello',
    description: 'Connect your Trello instance to sync tickets',
    icon: () => '',
    logo: '/app/integrations/square-icons/Trello.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['Ticketing'],
  },
  // Asset Management, IT/Hardware
  'snipe-it': {
    integration: 'snipe-it',
    operation: '',
    title: 'Snipe-IT',
    description: 'Connect your Snipe-IT instance to sync assets',
    icon: () => '',
    logo: '/app/integrations/square-icons/Snipe IT.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['Asset Management'],
  },
  bluetally: {
    integration: 'bluetally',
    operation: '',
    title: 'BlueTally',
    description: 'Connect your BlueTally instance to sync assets',
    icon: () => '',
    logo: '/app/integrations/square-icons/BlueTally.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['Asset Management'],
  },
  // ESG
  ecovadis: {
    integration: 'ecovadis',
    operation: '',
    title: 'EcoVadis',
    description: 'Connect your EcoVadis instance to sync ESG scores',
    icon: () => '',
    logo: '/app/integrations/square-icons/Ecovadis.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['ESG'],
  },
  // E-Signature
  'adobe-sign': {
    integration: 'adobe-sign',
    operation: 'adobe-sign-create-agreement',
    title: 'Adobe Sign',
    description: 'Upload a document to Adobe Sign and wait for it to be signed',
    icon: () => '/app/integrations/square-icons/Adobe Sign.svg',
    logo: '/app/integrations/square-icons/Adobe Sign.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['E-Signature'],
  },
  http: {
    integration: 'http',
    operation: 'http-custom-integration',
    title: 'Post (HTTP)',
    description: 'Custom integration',
    icon: (color) => `/app/integrations/icons/default-${color}.png`,
    logo: '/app/integrations/logos/http.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['Other'],
  },
  sftp: {
    integration: 'sftp',
    operation: 'sftp-upload-file',
    title: 'SFTP',
    description: 'Upload a file to an SFTP server',
    icon: () => '/app/integrations/square-icons/SFTP.svg',
    logo: '/app/integrations/square-icons/SFTP.svg',
    isLive: true,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['Other'],
  },
  // Public API
  'public-api': {
    integration: 'public-api',
    operation: '',
    title: 'Omnea Public API',
    description: 'Connect to our Public API instance to sync data',
    icon: () => '',
    logo: '/logo-without-text.svg',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'Native',
    categories: ['Public Api'],
  },
  ai: {
    integration: 'ai',
    operation: 'ai-generate-text',
    title: 'AI',
    description: 'Generate text using AI models from OpenAI, Anthropic, Google, and more',
    icon: () => '/app/integrations/square-icons/AI.svg',
    logo: '/app/integrations/square-icons/AI.svg',
    isLive: true,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['AI'],
  },
  // Pricing intelligence
  tropic: {
    integration: 'tropic',
    operation: 'tropic-price-benchmark',
    title: 'Price Intelligence',
    description: 'Run price intelligence analysis on supplier quotes during a request',
    icon: () => '/app/integrations/square-icons/Tropic.svg',
    logo: '/app/integrations/square-icons/Tropic.svg',
    isLive: false,
    isInRoadmap: true,
    integrationSource: 'ExtOps',
    categories: ['Pricing intelligence'],
  },
  // Punchout catalogues
  'punchout-generic': {
    integration: 'punchout-generic',
    operation: 'punchout-generic-create-punchout-setup-request',
    title: 'Punchout: Internal',
    description: 'Generic punchout catalogue integration',
    icon: (color) => `/app/integrations/icons/default-${color}.png`,
    logo: '/logo-without-text.svg',
    isLive: true,
    isInRoadmap: false, // Only real integrations/suppliers will ever be in the roadmap.
    integrationSource: 'ExtOps',
    categories: ['Other'],
    isOmnean: true,
  },
  'punchout-amazon-business': {
    integration: 'punchout-amazon-business',
    operation: 'punchout-amazon-business-create-punchout-setup-request',
    title: 'Punchout: Amazon Business',
    description: 'Amazon Business punchout catalogue integration',
    icon: () => '/app/integrations/square-icons/AmazonBusinessPunchout.svg',
    logo: '/app/integrations/square-icons/AmazonBusinessPunchout.svg',
    isLive: true,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['Other'],
    isOmnean: true,
  },
  'punchout-unite': {
    integration: 'punchout-unite',
    operation: 'punchout-unite-create-punchout-setup-request',
    title: 'Punchout: Unite',
    description: 'Unite punchout catalogue integration',
    icon: () => '/app/integrations/square-icons/UnitePunchout.svg',
    logo: '/app/integrations/square-icons/UnitePunchout.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['Other'],
    isOmnean: true,
  },
  'punchout-cdw': {
    integration: 'punchout-cdw',
    operation: 'punchout-cdw-create-punchout-setup-request',
    title: 'Punchout: CDW',
    description: 'CDW punchout catalogue integration',
    icon: () => '/app/integrations/square-icons/CDWPunchout.svg',
    logo: '/app/integrations/square-icons/CDWPunchout.svg',
    isLive: false,
    isInRoadmap: false,
    integrationSource: 'ExtOps',
    categories: ['Other'],
    isOmnean: true,
  },
} as const
